import axios from 'axios';
import { API_BASE_URL } from '../config';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle token expiration
    if (error.response && error.response.status === 401) {
      // If unauthorized, redirect to login
      // Clear notification service state (lazy import to avoid circular dependency)
      // import('../services/notificationService').then(module => {
      //   module.default.clear();
      // }).catch(err => console.error('Failed to clear notification service:', err));
      
      // Disconnect WebSocket (lazy import to avoid circular dependency)
      import('../services/websocketService').then(module => {
        module.default.disconnect();
      }).catch(err => console.error('Failed to disconnect websocket:', err));
      
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  getProfile: () => api.get('/api/auth/profile'),
  changePassword: (passwordData) => api.put('/api/auth/change-password', passwordData),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/api/dashboard/stats'),
  getActivity: () => api.get('/api/dashboard/activity'),
  getWeeklyActivity: () => api.get('/api/dashboard/weekly-activity'),
};

// Personnel API
export const personnelAPI = {
  getAll: (filters = {}) => api.get('/api/personnel', { params: filters }),
  getById: (id) => api.get(`/api/personnel/${id}`),
  create: (data) => api.post('/api/personnel', data),
  update: (id, data) => api.put(`/api/personnel/${id}`, data),
  toggleStatus: (id) => api.patch(`/api/personnel/${id}/toggle-status`),
  bulkUpload: (formData) => api.post('/api/personnel/bulk-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  downloadTemplate: () => api.get('/api/personnel/download-template', { responseType: 'blob' }),
  downloadCredentials: (id, resetPassword = false, includePassword = false) => api.get(`/api/personnel/${id}/credentials`, {
    params: { resetPassword, includePassword },
    responseType: 'blob'
  }),
  delete: (id) => api.delete(`/api/personnel/${id}`),
  getStats: (filters = {}) => api.get('/api/personnel/stats', { params: filters }),
  getAdminStats: (filters = {}) => api.get('/api/personnel/admin-stats', { params: filters }),
};

// Locations API (using /api/branches endpoint)
// Branches API
export const branchesAPI = {
  getAll: (filters = {}) => api.get('/api/branches', { params: filters }),
  getById: (id) => api.get(`/api/branches/${id}`),
  create: (data) => api.post('/api/branches', data),
  update: (id, data) => api.put(`/api/branches/${id}`, data),
  delete: (id) => api.delete(`/api/branches/${id}`),
  assignPersonnel: (id, personnelIds) => api.post(`/api/branches/${id}/assign-personnel`, { personnelIds }),
  // Export all branches, regions, and sites structure as Excel
  exportStructureExcel: () => api.get('/api/branches/export/structure/excel', { responseType: 'blob' }),
};

// Regions API
export const regionsAPI = {
  getAll: (filters = {}) => api.get('/api/regions', { params: filters }),
  getById: (id) => api.get(`/api/regions/${id}`),
  create: (data) => api.post('/api/regions', data),
  update: (id, data) => api.put(`/api/regions/${id}`, data),
  delete: (id) => api.delete(`/api/regions/${id}`),
  // assignSites supports optional orphan-handling fields:
  //   withOrphanCheck: when true, backend returns 409 if removing a site would
  //     leave it without a primary region; frontend can prompt the admin to pick.
  //   replacementPrimaries: { [siteId]: regionId } to set as the new primary.
  //   allowClearPrimary: when true, admin accepts clearing primary for any
  //     orphan site that wasn't given a replacement.
  assignSites: (id, siteIds, opts = {}) => api.post(
    `/api/regions/${id}/assign-sites`,
    { siteIds, ...opts }
  ),
  getAssignedSites: (id, siteIds) => api.get(`/api/regions/${id}/assigned-sites`, { siteIds }),

  assignPersonnel: (id, personnelIds) => api.post(`/api/regions/${id}/assign-personnel`, { personnelIds }),

  // Export region's assigned sites as Excel or PDF
  exportSitesExcel: (id) => api.get(`/api/regions/${id}/assigned-sites/export/excel`, { responseType: 'blob' }),
  exportSitesPdf: (id) => api.get(`/api/regions/${id}/assigned-sites/export/pdf`, { responseType: 'blob' }),
};

// Sites API
export const sitesAPI = {
  getAll: (filters = {}) => api.get('/api/sites', { params: filters }),
  getAssigned: () => api.get('/api/sites/assigned'),
  getById: (id) => api.get(`/api/sites/${id}`),
  create: (data) => api.post('/api/sites', data),
  update: (id, data) => api.put(`/api/sites/${id}`, data),
  delete: (id) => api.delete(`/api/sites/${id}`),
  getPersonnel: (id) => api.get(`/api/sites/${id}/personnel`),
  assignPersonnel: (id, personnelIds) => api.post(`/api/sites/${id}/assign-personnel`, { personnelIds }),
  getStats: (filters = {}) => api.get('/api/sites/stats', { params: filters }),
  getAdminStats: (filters = {}) => api.get('/api/statistics/admin/sites', { params: filters }),
  validateGPS: (gpsInput) => api.post('/api/sites/validate-gps', { gpsInput }),
  exportFiltered: (filters = {}) => api.post('/api/sites/export-filtered', filters, { responseType: 'blob' }),
};

// Check-ins API
export const checkinsAPI = {
  getActive: () => api.get('/api/checkins/active'),
  getHistory: (filters = {}) => api.get('/api/checkins/history', { params: filters }),
  checkIn: (siteId, location) =>
    api.post('/api/checkins', {
      siteId: Number(siteId),
      latitude: location.latitude,
      longitude: location.longitude
    }),

  checkOut: (locationData = {}) => api.put('/api/checkins/checkout', locationData),
  getAllActive: () => api.get('/api/checkins/admin/checkins/active'),
  getStats: (filters = {}) => api.get('/api/checkins/stats', { params: filters }),
  // Admin endpoint to get all check-ins with filters
  getAllCheckIns: (filters = {}) => api.get('/api/checkins/admin/all', { params: filters }),
};

// Reports API
export const reportsAPI = {
  submit: (reportData) => {
    const formData = new FormData();

    // Add text fields
    formData.append('checkInId', reportData.checkInId);
    formData.append('content', reportData.content);

    // Add attachment if present
    if (reportData.attachment) {
      formData.append('attachment', reportData.attachment);
    }

    return api.post('/api/reports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Save draft report with photos to backend
  saveDraft: (draftData) => {
    const formData = new FormData();

    formData.append('checkInId', draftData.checkInId);
    formData.append('content', draftData.content || '');

    // Add captured photos as blobs
    if (draftData.capturedImages && Array.isArray(draftData.capturedImages)) {
      draftData.capturedImages.forEach((imageDataUrl, index) => {
        // Convert data URL to blob
        fetch(imageDataUrl)
          .then(res => res.blob())
          .then(blob => {
            formData.append('draftPhotos', blob, `draft-photo-${index + 1}.jpg`);
          });
      });
    }

    // Add attachment if present
    if (draftData.attachment) {
      formData.append('attachment', draftData.attachment);
    }

    return api.post('/api/reports/draft', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Get draft report for a check-in session
  getDraft: (checkInId) => api.get(`/api/reports/draft/${checkInId}`),
  getUserReports: (filters = {}) => api.get('/api/reports/user', { params: filters }),
  getById: (id) => api.get(`/api/reports/${id}`),
  downloadAttachment: (id) => api.get(`/api/reports/${id}/attachment`, { responseType: 'blob' }),
  getAllReports: (filters = {}) => api.get('/api/reports', { params: filters }),
  exportReportPdf: (reportId) => api.get(`/api/reports/${reportId}/export-pdf`, { responseType: 'blob' }),
  exportAllReportsPdf: (filters = {}) => api.get('/api/reports/export-all', {
    params: filters,
    responseType: 'blob'
  }),
  exportSitesReportsPdf: (filters = {}) => api.get('/api/reports/export-sites-reports-pdf', {
    params: filters,
    responseType: 'blob'
  }),
  exportSitesReportsExcel: (filters = {}) => api.get('/api/reports/export-sites-reports-excel', {
    params: filters,
    responseType: 'blob'
  }),
};

// Statistics API
export const statisticsAPI = {
  getUserStats: (timeRange = 'week') => api.get(`/api/statistics/user?timeRange=${timeRange}`),
  getAdminStats: (filters = {}) => api.get('/api/statistics/admin', { params: filters }),
  getUnvisitedSites: (filters = {}) => api.get('/api/statistics/unvisited-sites', { params: filters }),
  exportUnvisitedSitesPdf: (filters) => api.get('/api/statistics/unvisited-sites/export/pdf', { params: filters, responseType: 'blob' }),
  exportUnvisitedSitesExcel: (filters) => api.get('/api/statistics/unvisited-sites/export/excel', { params: filters, responseType: 'blob' }),

  // NEW: Specialized Analytics Exports
  exportExecutiveSummary: (filters = {}) => api.get('/api/statistics/export-executive-summary', {
    params: filters,
    responseType: 'blob'
  }),
  exportComplianceReport: (filters = {}) => api.get('/api/statistics/export-compliance-report', {
    params: filters,
    responseType: 'blob'
  }),
  exportTimesheet: (filters = {}) => api.get('/api/statistics/export-timesheet', {
    params: filters,
    responseType: 'blob'
  }),

  // Analytics Dashboard Excel (comprehensive multi-sheet export)
  exportExcel: (filters = {}) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const endpoint = user.role === 'personnel' ? '/api/statistics/user/export-excel' : '/api/statistics/export-excel';
    return api.get(endpoint, {
      params: filters,
      responseType: 'blob'
    });
  },

  // Personnel Performance exports (aggregated analytics)
  exportPersonnelPerformanceExcel: (filters = {}) => api.get('/api/statistics/export-personnel-performance-excel', {
    params: filters,
    responseType: 'blob'
  }),
  exportPersonnelPerformancePdf: (filters = {}) => api.get('/api/statistics/export-personnel-performance-pdf', {
    params: filters,
    responseType: 'blob'
  }),

  // DEPRECATED: Basic activity PDF export (use Reports exports instead for detailed operational data)
  exportPdf: (filters = {}) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const endpoint = user.role === 'personnel' ? '/api/statistics/user/export' : '/api/statistics/export';
    return api.get(endpoint, {
      params: filters,
      responseType: 'blob'
    });
  },

  getActivity: (filters = {}) => api.get('/api/statistics/activity', { params: filters }),
  getSummary: (filters = {}) => api.get('/api/statistics/summary', { params: filters }),
  getChartData: (filters = {}) => api.get('/api/statistics/charts', { params: filters }),
};

// Sub-admin API
export const subadminAPI = {
  getAll: () => api.get('/api/subadmins'),
  getById: (id) => api.get(`/api/subadmins/${id}`),
  create: (data) => api.post('/api/subadmins', data),
  update: (id, data) => api.put(`/api/subadmins/${id}`, data),
  updatePassword: (id, password) => api.put(`/api/subadmins/${id}/password`, { password }),
  toggleStatus: (id) => api.put(`/api/subadmins/${id}/toggle-status`)
};

// Clients API
export const clientsAPI = {
  getAll: (filters = {}) => api.get('/api/clients', { params: filters }),
  getMyClients: () => api.get('/api/clients/my-clients'),
  getById: (id) => api.get(`/api/clients/${id}`),
  create: (data) => api.post('/api/clients', data),
  update: (id, data) => api.put(`/api/clients/${id}`, data),
  delete: (id) => api.delete(`/api/clients/${id}`),
  exportPDF: (filters = {}) => api.get('/api/clients/export-pdf', {
    params: filters,
    responseType: 'blob'
  }),
  exportExcel: (filters = {}) => api.get('/api/clients/export-excel', {
    params: filters,
    responseType: 'blob'
  })
};

// Coverage API
export const coverageAPI = {
  getAll: (filters = {}) => api.get('/api/coverage', { params: filters }),
  getById: (id) => api.get(`/api/coverage/${id}`),
  create: (data) => api.post('/api/coverage', data),
  update: (id, data) => api.put(`/api/coverage/${id}`, data),
  delete: (id) => api.delete(`/api/coverage/${id}`),
  updateStatus: (id, status) => api.patch(`/api/coverage/${id}/status`, { status }),
  exportPDF: (filters = {}) => api.get('/api/coverage/export/pdf', {
    params: filters,
    responseType: 'blob'
  }),
  exportExcel: (filters = {}) => api.get('/api/coverage/export/excel', {
    params: filters,
    responseType: 'blob'
  })
};

// Incident Reports API
export const incidentReportsAPI = {
  // Personnel - Draft Management
  saveDraft: (data) => api.post('/api/incident-reports/drafts', data),
  getDrafts: () => api.get('/api/incident-reports/drafts'),
  deleteDraft: (id) => api.delete(`/api/incident-reports/drafts/${id}`),
  
  // Personnel - Photo Management
  uploadPhoto: (formData) => api.post('/api/incident-reports/drafts/photos', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  deletePhoto: (photoPath, draftId) => api.delete('/api/incident-reports/drafts/photos', {
    data: { photoPath, draftId }
  }),
  
  // Personnel - Document Management
  uploadDocument: (formData) => api.post('/api/incident-reports/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  deleteDocument: (documentPath, reportId) => api.delete('/api/incident-reports/documents', {
    data: { documentPath, reportId }
  }),
  
  // Personnel - Submit Report
  submit: (data) => api.post('/api/incident-reports/submit', data),
  
  // Personnel - My Reports
  getMyReports: (filters = {}) => api.get('/api/incident-reports/my-reports', { params: filters }),
  getById: (id) => api.get(`/api/incident-reports/${id}`),
  
  // Admin - Reports Management
  getAllReports: (filters = {}) => api.get('/api/incident-reports/admin/all', { params: filters }),
  getStats: () => api.get('/api/incident-reports/admin/stats'),
  getReportById: (id) => api.get(`/api/incident-reports/admin/${id}`),
  updateStatus: (id, data) => api.patch(`/api/incident-reports/admin/${id}/status`, data),
  updateReport: (id, data) => api.patch(`/api/incident-reports/admin/${id}`, data),
  deleteReport: (id) => api.delete(`/api/incident-reports/admin/${id}`),
  
  // Admin - Export
  exportCSV: (filters = {}) => api.get('/api/incident-reports/admin/export/csv', {
    params: filters,
    responseType: 'blob'
  }),
  exportPDF: (filters = {}) => api.get('/api/incident-reports/admin/export/pdf', {
    params: filters,
    responseType: 'blob'
  }),
  exportIndividualReportPDF: (id) => api.get(`/api/incident-reports/admin/${id}/export/pdf`, {
    responseType: 'blob'
  }),

  // Comments
  getComments: (id) => api.get(`/api/incident-reports/${id}/comments`),
  addComment: (id, data) => api.post(`/api/incident-reports/${id}/comments`, data),
};

// Special Assignments API
export const specialAssignmentsAPI = {
  // Personnel - Create and Manage Assignments
  create: (data) => api.post('/api/special-assignments', data),
  getMyAssignments: (filters = {}) => api.get('/api/special-assignments/my-assignments', { params: filters }),
  getById: (id) => api.get(`/api/special-assignments/${id}`),
  updateStatus: (id, status, notes = '') => api.patch(`/api/special-assignments/${id}/status`, { status, notes }),
  
  // Admin - View All Assignments
  getAll: (filters = {}) => api.get('/api/special-assignments/admin/all', { params: filters }),
  getStats: () => api.get('/api/special-assignments/admin/stats'),
  
  // Admin - Assign to Personnel
  assignToPersonnel: (data) => api.post('/api/special-assignments/admin/assign', data),
  
  // Admin - Review Personnel Submissions
  approveSubmission: (id, notes = '') => api.patch(`/api/special-assignments/admin/${id}/approve`, { notes }),
  rejectSubmission: (id, notes = '') => api.patch(`/api/special-assignments/admin/${id}/reject`, { notes }),
  
  // Admin - Verify and Close Completed Assignments
  verifyCompletion: (id, notes = '') => api.patch(`/api/special-assignments/admin/${id}/verify`, { notes }),
  
  // Search Personnel (for assignment dropdown)
  searchPersonnel: (search = '') => api.get('/api/personnel/search', { params: { search } }),
  
  // Delete
  delete: (id) => api.delete(`/api/special-assignments/${id}`),
  
  // Export
  exportCSV: (filters = {}) => api.get('/api/special-assignments/admin/export/csv', {
    params: filters,
    responseType: 'blob'
  }),
  exportPDF: (filters = {}) => api.get('/api/special-assignments/admin/export/pdf', {
    params: filters,
    responseType: 'blob'
  })
};

// Notification API
export const notificationAPI = {
  getAll: (params = {}) => {
    const { limit = 50, offset = 0, unreadOnly = false } = params;
    return api.get('/api/notifications', { 
      params: { limit, offset, unreadOnly } 
    });
  },
  markAsRead: (id) => api.patch(`/api/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/api/notifications/read-all'),
  delete: (id) => api.delete(`/api/notifications/${id}`)
};

const apiExports = {
  authAPI,
  personnelAPI,
  sitesAPI,
  checkinsAPI,
  reportsAPI,
  statisticsAPI,
  dashboardAPI,
  subadminAPI,

  branchesAPI,
  regionsAPI,

  clientsAPI,
  coverageAPI,
  incidentReportsAPI,
  specialAssignmentsAPI,
  
  notificationAPI

};

export default apiExports;
